import { layerGeomType } from './style-defaults';

/**
 * The layer list the properties panel works on, plus the active-layer rules.
 * Descriptor: { id, name, kind: 'vector' | 'raster' | 'tiles', origin, visible, features,
 *   geomType, featureCount, datasetId, dataset, resultKind, ref }. `id` is the key of
 * `layerStyles` for vector layers (usgs-<id> / imported id / result id), the raster
 * layer id (`raster-<dataset>`) or the tile layer id (`vt-<dataset>-<kind>`).
 */
export function buildPropertyLayers({ layers = [], usgsData = {}, importedLayers = [], resultLayers = [], rasterLayers = [], vtLayers = [] } = {}) {
  const out = [];
  const vector = (id, name, geojson, visible, extra) => {
    const features = geojson?.features || [];
    out.push({
      id, name, kind: 'vector', visible, features, geomType: layerGeomType(features),
      featureCount: features.length, ...extra,
    });
  };
  Object.entries(usgsData).forEach(([lid, gj]) => {
    if (!gj?.features?.length) return;
    const l = layers.find((x) => x.id === lid);
    vector(`usgs-${lid}`, l?.name || lid, gj, !!l?.visible, { origin: 'demo', method: l?.method });
  });
  importedLayers.forEach((l) => vector(l.id, l.name, l.geojson, l.visible !== false, {
    origin: 'file', fileName: l.fileName || l.name, xyz: !!l.xyz, importInfo: l.xyz || null,
  }));
  resultLayers.forEach((r) => vector(r.id, r.name, r.geojson, r.visible !== false, r.dataset
    ? { origin: 'dataset', dataset: r.dataset, datasetId: r.dataset.id, resultKind: r.kind }
    : { origin: 'analysis', resultKind: r.kind }));
  rasterLayers.forEach((r) => out.push({
    id: r.id, name: r.name, kind: 'raster', origin: 'raster', visible: !!r.visible, features: [],
    geomType: null, featureCount: 0, datasetId: r.datasetId, bounds: r.bounds || null, ref: r,
  }));
  vtLayers.forEach((v) => out.push({
    id: v.id, name: v.name, kind: 'tiles', origin: 'tiles', visible: !!v.visible, features: [],
    geomType: v.kind === 'electrode' || v.kind === 'pseudo_point' || v.kind === 'xyz_point' ? 'point' : null,
    featureCount: Number(v.featureCount) || 0, datasetId: v.datasetId, bounds: v.bounds || null, ref: v,
  }));
  return out;
}

/** The layer the panel shows: the requested one if it exists, else the first visible, else the first. */
export function pickActiveLayer(list, requestedId) {
  if (!list?.length) return null;
  return list.find((l) => l.id === requestedId) || list.find((l) => l.visible) || list[0];
}

const MAP_LAYER_PREFIXES = [
  ['usgs-pts-', (s) => `usgs-${s}`], ['usgs-line-', (s) => `usgs-${s}`], ['usgs-fill-', (s) => `usgs-${s}`],
  ['imp-pts-', (s) => s], ['imp-line-', (s) => s], ['imp-fill-', (s) => s],
  ['result-pts-', (s) => s], ['result-line-', (s) => s], ['result-fill-', (s) => s],
];

/**
 * The properties-panel layer id for a rendered MapLibre layer id (a clicked feature's
 * `layer.id`), or null for layers the panel does not style (survey lines, soundings ...).
 * Tile layer ids end in `-pts` and already equal `<id>-pts` of their descriptor.
 */
export function layerIdFromMapLayer(mapLayerId, knownIds = []) {
  if (!mapLayerId) return null;
  for (const [prefix, toId] of MAP_LAYER_PREFIXES) {
    if (mapLayerId.startsWith(prefix)) return toId(mapLayerId.slice(prefix.length));
  }
  if (mapLayerId.startsWith('vt-') && mapLayerId.endsWith('-pts')) {
    const id = mapLayerId.slice(0, -'-pts'.length);
    return !knownIds.length || knownIds.includes(id) ? id : null;
  }
  return null;
}
